/**
 * PB-BRI-3 — Single-flight AI generation session gate.
 * One in-flight Character job per order path; Edit locked while generating;
 * SUB leave blocked until terminal or explicit Cancel.
 * PB-AOG-1 — Offers/Add-ons doorway blocked while Character job queued/running.
 * PB-AOG-2 — Doorway also hard-blocks on fail/timeout pending Retry; Cancel escapes.
 * PB-AOG-5 — offersDoorwayOpen readiness owner on nav belt; no maxSteps bypass.
 * PB-AOG-6 — occupancy redirect, draft resume clamp, required-AI Cancel doorway split.
 * PB-AOG-9 — SUB in-row forward vs SUB leave vs main doorway nav intents.
 */

import { parseAiFieldSettings } from '@theme/pb-field-ai-image';
import {
  purgeOrphanAiImageByLabel,
  resolveMainProductAiLabels,
  resolveSubFlowAiLabels,
} from '@theme/pb-ai-catalog-scope';
import { isCartEditEntryManifest, readEntryManifest } from '@theme/pb-entry-manifest';
import { STEP_KINDS } from '@theme/pb-step-kinds';
import { getStepKind, resolveStepAfterDelta, resolveStepMap } from '@theme/pb-navigation';

export const EDIT_BLOCK_MSG =
  'Character is still being created. Wait or cancel it first.';
export const SUB_LEAVE_BLOCK_MSG =
  'Finish or cancel character creation before leaving.';
export const OFFERS_DOORWAY_BLOCK_MSG =
  'Character is still being created. Wait or cancel before opening Offers or Add-ons.';
export const OFFERS_DOORWAY_FAIL_BLOCK_MSG =
  'Character creation failed. Retry or cancel before opening Offers or Add-ons.';

/** Navigation intent for async UX + doorway gates (PB-AOG-9). */
export const NAV_INTENTS = Object.freeze({
  IN_ROW_FORWARD: 'inRowForward',
  IN_ROW_BACK: 'inRowBack',
  SUB_LEAVE: 'subLeave',
  MAIN_DOORWAY_FORWARD: 'mainDoorwayForward',
  NEUTRAL: 'neutral',
});

export function isActiveJobStatus(status) {
  const s = String(status || 'idle').trim();
  return s === 'queued' || s === 'running';
}

export function isFailedPendingRetryStatus(status, saved) {
  const s = String(status || 'idle').trim();
  if (s === 'failed') return true;
  if (saved && saved.aiError) return true;
  return false;
}

export function isSavedEntryRunning(saved) {
  if (!saved || typeof saved !== 'object') return false;
  if (saved.isGenerating) return true;
  return isActiveJobStatus(saved.jobStatus);
}

function resolveBuilderData(options) {
  if (options && options.builderData) return options.builderData;
  if (typeof window !== 'undefined') return window.BUILDER_DATA;
  return null;
}

function isAiImageFieldConfig(field) {
  return String(field?.type || '')
    .toLowerCase()
    .includes('ai image');
}

function isFieldRequiredAi(field) {
  if (!field || !isAiImageFieldConfig(field)) return false;
  const cfg = parseAiFieldSettings(field);
  return cfg.required === true || cfg.required === 'true';
}

function resolveAiFieldForLabel(label, options) {
  const data = resolveBuilderData(options);
  if (!data || !label) return null;
  const fields = data.mainProduct?.config || data.mainProduct?.personalization || [];
  if (!Array.isArray(fields)) return null;
  return fields.find((field) => field && field.label === label) || null;
}

function hasSuccessfulCharacter(saved) {
  const urls = saved?.characterUrls;
  if (!Array.isArray(urls)) return false;
  return urls.some((url) => url != null && String(url).trim() !== '');
}

/** Required-AI explicit Cancel without Character keeps doorway closed (optional-AI Cancel escapes). */
export function isRequiredAiCancelledDoorwayBlocked(saved, options) {
  if (!saved || typeof saved !== 'object') return false;
  const status = String(saved.jobStatus || 'idle').trim();
  if (status !== 'cancelled') return false;
  if (hasSuccessfulCharacter(saved)) return false;
  const label = options?.label;
  const field = resolveAiFieldForLabel(label, options);
  if (!field) return false;
  return isFieldRequiredAi(field);
}

/** True when Offers/Add-ons doorway must stay closed (running, fail pending Retry, or required-AI Cancel). */
export function isSavedEntryDoorwayBlocked(saved, options) {
  if (!saved || typeof saved !== 'object') return false;
  if (saved.isGenerating) return true;
  if (isActiveJobStatus(saved.jobStatus)) return true;
  if (isFailedPendingRetryStatus(saved.jobStatus, saved)) return true;
  return isRequiredAiCancelledDoorwayBlocked(saved, options);
}

function forEachScopedByLabelEntry(byLabel, allowedLabels, visit, predicate) {
  if (!byLabel || typeof byLabel !== 'object') return false;
  const labels = byLabel.byLabel && typeof byLabel.byLabel === 'object' ? byLabel.byLabel : byLabel;
  let found = false;
  Object.entries(labels).forEach(([label, saved]) => {
    if (!allowedLabels.has(label)) return;
    if (predicate(saved, label)) {
      found = true;
      visit(saved, label);
    }
  });
  return found;
}

function forEachScopedByLabelStore(byLabel, allowedLabels, visit, predicate) {
  if (!byLabel || typeof byLabel !== 'object') return false;
  const labels = byLabel.byLabel && typeof byLabel.byLabel === 'object' ? byLabel.byLabel : byLabel;
  const matches = predicate || isSavedEntryRunning;
  let found = false;
  Object.entries(labels).forEach(([label, saved]) => {
    if (!allowedLabels.has(label)) return;
    if (matches(saved)) {
      found = true;
      visit(saved);
    }
  });
  return found;
}

function forEachByLabelEntry(byLabel, visit, predicate) {
  if (!byLabel || typeof byLabel !== 'object') return false;
  const labels = byLabel.byLabel && typeof byLabel.byLabel === 'object' ? byLabel.byLabel : byLabel;
  let found = false;
  Object.entries(labels).forEach(([label, saved]) => {
    if (predicate(saved, label)) {
      found = true;
      visit(saved, label);
    }
  });
  return found;
}

function forEachByLabelStore(byLabel, visit, predicate) {
  if (!byLabel || typeof byLabel !== 'object') return false;
  const labels = byLabel.byLabel && typeof byLabel.byLabel === 'object' ? byLabel.byLabel : byLabel;
  const matches = predicate || isSavedEntryRunning;
  let found = false;
  Object.values(labels).forEach((saved) => {
    if (matches(saved)) {
      found = true;
      visit(saved);
    }
  });
  return found;
}

function scanBundleConfigurations(builderState, visit, predicate) {
  const configs = builderState?.bundle?.configurations;
  if (!configs || typeof configs !== 'object') return false;
  const matches = predicate || isSavedEntryRunning;
  let found = false;
  Object.values(configs).forEach((bundleSlots) => {
    if (!bundleSlots || typeof bundleSlots !== 'object') return;
    Object.values(bundleSlots).forEach((slot) => {
      const props = slot?.properties;
      if (!props || typeof props !== 'object') return;
      const jobStatus = props._pbAiJobStatus != null ? String(props._pbAiJobStatus).trim() : '';
      const saved = {
        jobStatus,
        aiError: props._pbAiError != null ? String(props._pbAiError) : '',
      };
      if (matches(saved)) {
        found = true;
        visit({ ...saved, source: 'bundle-config' });
      }
    });
  });
  return found;
}

function domWrapBlocksDoorway(state, options) {
  if (!state || typeof state !== 'object') return false;
  const label = state.label || state.fieldLabel || '';
  return isSavedEntryDoorwayBlocked(state, { ...(options || {}), label });
}

/**
 * Scan live DOM wraps for in-flight generation (authoritative during SUB edit).
 * @param {string[]} [rootSelectors]
 */
export function scanDomForRunningGeneration(rootSelectors) {
  if (typeof document === 'undefined') return null;
  const selectors = rootSelectors || ['#main-flow-container', '#sub-flow-container'];
  for (let i = 0; i < selectors.length; i += 1) {
    const root = document.querySelector(selectors[i]);
    if (!root || !root.querySelectorAll) continue;
    const wraps = root.querySelectorAll('.pb-ai-image-wrap');
    for (let j = 0; j < wraps.length; j += 1) {
      const wrap = wraps[j];
      const state = wrap._pbAiState;
      if (state && (state.isGenerating || isActiveJobStatus(state.jobStatus))) {
        return { wrap, state, rootSelector: selectors[i] };
      }
    }
  }
  return null;
}

/**
 * Scan live DOM wraps that block Offers/Add-ons doorway (running or fail pending Retry).
 * @param {string[]} [rootSelectors]
 */
export function scanDomForDoorwayBlock(rootSelectors) {
  if (typeof document === 'undefined') return null;
  const selectors = rootSelectors || ['#main-flow-container', '#sub-flow-container'];
  for (let i = 0; i < selectors.length; i += 1) {
    const root = document.querySelector(selectors[i]);
    if (!root || !root.querySelectorAll) continue;
    const wraps = root.querySelectorAll('.pb-ai-image-wrap');
    for (let j = 0; j < wraps.length; j += 1) {
      const wrap = wraps[j];
      const state = wrap._pbAiState;
      if (domWrapBlocksDoorway(state)) {
        return { wrap, state, rootSelector: selectors[i] };
      }
    }
  }
  return null;
}

/**
 * True when any Character generation is queued/running on the order path.
 * @param {object} [builderState]
 * @param {{ includeDom?: boolean }} [options]
 */
export function hasOrderPathGenerationRunning(builderState, options) {
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state) return false;
  purgeOrphanAiImageByLabel(state, { builderData: resolveBuilderData(options) });

  const data = resolveBuilderData(options);
  const mainLabels = resolveMainProductAiLabels(data);
  if (
    state.aiImageByLabel &&
    forEachScopedByLabelStore(state.aiImageByLabel, mainLabels, () => {})
  ) {
    return true;
  }
  if (
    state.mode === 'SUB' &&
    state.sub?.isQueueItem &&
    state.sub?.aiImageByLabel &&
    forEachScopedByLabelStore(
      state.sub.aiImageByLabel,
      resolveSubFlowAiLabels(state),
      () => {}
    )
  ) {
    return true;
  }
  if (scanBundleConfigurations(state, () => {})) return true;

  if (options && options.includeDom === false) return false;
  return !!scanDomForRunningGeneration();
}

/**
 * True when any order-path Character job failed and is pending Retry.
 * @param {object} [builderState]
 * @param {{ includeDom?: boolean }} [options]
 */
export function hasOrderPathFailedPendingRetry(builderState, options) {
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state) return false;
  purgeOrphanAiImageByLabel(state, { builderData: resolveBuilderData(options) });

  const failedOnly = (saved) =>
    !saved?.isGenerating &&
    !isActiveJobStatus(saved?.jobStatus) &&
    isFailedPendingRetryStatus(saved?.jobStatus, saved);

  const data = resolveBuilderData(options);
  const mainLabels = resolveMainProductAiLabels(data);
  if (
    state.aiImageByLabel &&
    forEachScopedByLabelStore(state.aiImageByLabel, mainLabels, () => {}, failedOnly)
  ) {
    return true;
  }
  if (
    state.mode === 'SUB' &&
    state.sub?.isQueueItem &&
    state.sub?.aiImageByLabel &&
    forEachScopedByLabelStore(
      state.sub.aiImageByLabel,
      resolveSubFlowAiLabels(state),
      () => {},
      failedOnly
    )
  ) {
    return true;
  }
  if (scanBundleConfigurations(state, () => {}, failedOnly)) return true;

  if (options && options.includeDom === false) return false;
  const live = scanDomForDoorwayBlock();
  if (!live) return false;
  const domLabel = live.wrap?.dataset?.label || '';
  if (
    domLabel &&
    !mainLabels.has(domLabel) &&
    !(state.mode === 'SUB' && resolveSubFlowAiLabels(state).has(domLabel))
  ) {
    return false;
  }
  return !!(live && isFailedPendingRetryStatus(live.state?.jobStatus, live.state));
}

/**
 * True when Offers/Add-ons doorway must stay closed on the order path.
 * @param {object} [builderState]
 * @param {{ includeDom?: boolean }} [options]
 */
export function hasOrderPathDoorwayBlocked(builderState, options) {
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state) return false;
  const doorwayOpts = { ...(options || {}), builderData: resolveBuilderData(options) };
  purgeOrphanAiImageByLabel(state, { builderData: doorwayOpts.builderData });
  const blocked = (saved, label) => isSavedEntryDoorwayBlocked(saved, { ...doorwayOpts, label });
  const data = doorwayOpts.builderData;
  const mainLabels = resolveMainProductAiLabels(data);

  if (state.aiImageByLabel && forEachScopedByLabelEntry(state.aiImageByLabel, mainLabels, () => {}, blocked)) {
    return true;
  }
  if (
    state.mode === 'SUB' &&
    state.sub?.isQueueItem &&
    state.sub?.aiImageByLabel &&
    forEachScopedByLabelEntry(
      state.sub.aiImageByLabel,
      resolveSubFlowAiLabels(state),
      () => {},
      blocked
    )
  ) {
    return true;
  }
  if (scanBundleConfigurations(state, () => {}, blocked)) return true;

  if (options && options.includeDom === false) return false;
  const live = scanDomForDoorwayBlock();
  if (!live?.state) return false;
  const domLabel = live.wrap?.dataset?.label || '';
  if (domLabel && !mainLabels.has(domLabel) && !(state.mode === 'SUB' && resolveSubFlowAiLabels(state).has(domLabel))) {
    return false;
  }
  return isSavedEntryDoorwayBlocked(live.state, { ...doorwayOpts, label: domLabel });
}

/** Generation running in the active SUB edit session. */
export function hasSubContextGenerationRunning(builderState) {
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state || state.mode !== 'SUB') return false;
  const subLabels = resolveSubFlowAiLabels(state);
  if (
    state.sub?.aiImageByLabel &&
    forEachScopedByLabelStore(state.sub.aiImageByLabel, subLabels, () => {})
  ) {
    return true;
  }
  const live = scanDomForRunningGeneration(['#sub-flow-container']);
  if (!live?.wrap?.dataset?.label) return !!live;
  return subLabels.has(live.wrap.dataset.label);
}

function resolveActiveNavStep(builderState, navOpts) {
  const opts = navOpts || {};
  if (opts.currentStep != null) return Number(opts.currentStep) || 1;
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state) return 1;
  const ctx = state.mode === 'SUB' ? state.sub : state.main;
  return Number(ctx?.step) || 1;
}

function resolveActiveNavMaxSteps(builderState) {
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state) return 1;
  const ctx = state.mode === 'SUB' ? state.sub : state.main;
  return Number(ctx?.maxSteps) || Number(ctx?.step) || 1;
}

/**
 * Classify navigation intent — SUB in-row forward vs leave vs main doorway.
 * @param {object} [builderState]
 * @param {{ delta?: number, targetStep?: number, currentStep?: number, data?: object, deps?: object, navContext?: object }} [navOpts]
 */
export function classifyNavigationIntent(builderState, navOpts) {
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state) return NAV_INTENTS.NEUTRAL;

  const opts = navOpts || {};
  const delta = opts.delta != null ? (opts.delta === -1 ? -1 : 1) : null;
  const targetStep = opts.targetStep != null ? Number(opts.targetStep) : null;
  const currentStep = resolveActiveNavStep(state, opts);
  const maxSteps = resolveActiveNavMaxSteps(state);

  if (state.mode === 'MAIN') {
    if (
      shouldBlockOffersAddonsNavigation({
        builderState: state,
        currentStep,
        delta: delta ?? 1,
        targetStep,
        data: opts.data,
        deps: opts.deps,
        navContext: opts.navContext,
      })
    ) {
      return NAV_INTENTS.MAIN_DOORWAY_FORWARD;
    }
    return NAV_INTENTS.NEUTRAL;
  }

  if (state.mode !== 'SUB') return NAV_INTENTS.NEUTRAL;

  if (targetStep != null && !Number.isNaN(targetStep) && targetStep !== currentStep) {
    if (targetStep < currentStep) {
      if (currentStep <= 1) return NAV_INTENTS.SUB_LEAVE;
      return NAV_INTENTS.IN_ROW_BACK;
    }
    if (currentStep >= maxSteps) return NAV_INTENTS.SUB_LEAVE;
    return NAV_INTENTS.IN_ROW_FORWARD;
  }

  if (delta === 1) {
    if (currentStep >= maxSteps) return NAV_INTENTS.SUB_LEAVE;
    return NAV_INTENTS.IN_ROW_FORWARD;
  }
  if (delta === -1) {
    if (currentStep <= 1) return NAV_INTENTS.SUB_LEAVE;
    return NAV_INTENTS.IN_ROW_BACK;
  }

  return NAV_INTENTS.NEUTRAL;
}

/**
 * True when SUB generation belt blocks this navigation (leave / finish — not in-row forward).
 * @param {object} [builderState]
 * @param {{ delta?: number, targetStep?: number, currentStep?: number, navIntent?: string }} [navOpts]
 */
export function blocksSubGenerationNavigation(builderState, navOpts) {
  if (!hasSubContextGenerationRunning(builderState)) return false;
  const intent =
    (navOpts && navOpts.navIntent) || classifyNavigationIntent(builderState, navOpts);
  if (intent === NAV_INTENTS.IN_ROW_FORWARD || intent === NAV_INTENTS.IN_ROW_BACK) {
    return false;
  }
  return true;
}

export function isOffersAddonsStepKind(kind) {
  return kind === STEP_KINDS.OFFER || kind === STEP_KINDS.ADDON;
}

export function wouldEnterOffersAddonsDoorway(currentStep, delta, data, deps, navContext) {
  const dir = delta === -1 ? -1 : 1;
  if (dir !== 1) return false;
  const target = resolveStepAfterDelta(currentStep, dir, data, deps, navContext);
  if (target <= currentStep) return false;
  const kind = getStepKind(target, data, deps, navContext);
  return isOffersAddonsStepKind(kind);
}

export function isOffersAddonsTargetStep(targetStep, data, deps, navContext) {
  const kind = getStepKind(targetStep, data, deps, navContext);
  return isOffersAddonsStepKind(kind);
}

/**
 * True when Offers/Add-ons doorway may open (Character ready, or optional-AI Cancel escape).
 * @param {object} [builderState]
 * @param {{ includeDom?: boolean, builderData?: object }} [options]
 */
export function offersDoorwayOpen(builderState, options) {
  return !hasOrderPathDoorwayBlocked(builderState, options);
}

/** True while any order-path Character job blocks the Offers/Add-ons doorway. */
export function blocksOffersAddonsDoorway(builderState, options) {
  return !offersDoorwayOpen(builderState, options);
}

export function shouldBlockOffersAddonsNavigation(opts) {
  const options = opts || {};
  if (!blocksOffersAddonsDoorway(options.builderState, { includeDom: true })) return false;
  if (options.targetStep != null) {
    return isOffersAddonsTargetStep(
      options.targetStep,
      options.data,
      options.deps,
      options.navContext
    );
  }
  if (options.currentStep != null && options.delta != null) {
    return wouldEnterOffersAddonsDoorway(
      options.currentStep,
      options.delta,
      options.data,
      options.deps,
      options.navContext
    );
  }
  return false;
}

export function blocksForwardToOffersAddonsDoorway(currentStep, builderState, navOpts) {
  if (!blocksOffersAddonsDoorway(builderState, { includeDom: false, builderData: navOpts?.data })) return false;
  const nav = navOpts || {};
  return wouldEnterOffersAddonsDoorway(currentStep, 1, nav.data, nav.deps, nav.navContext);
}

export function resolveLastPersonalizeStepIndex(data, deps, navContext) {
  const map = resolveStepMap(data, deps, navContext);
  for (let i = map.length - 1; i >= 0; i -= 1) {
    if (map[i].kind === STEP_KINDS.PERSONALIZE) return map[i].index;
  }
  return map.length ? map[0].index : 1;
}

export function resolvePersonalizeStepForLabel(label, data, deps, navContext) {
  if (!label) return null;
  const fields = data?.mainProduct?.config || data?.mainProduct?.personalization || [];
  if (!Array.isArray(fields)) return null;
  const fieldIdx = fields.findIndex((field) => field && field.label === label);
  if (fieldIdx < 0) return null;
  const map = resolveStepMap(data, deps, navContext);
  let personalizeCount = 0;
  for (let i = 0; i < map.length; i += 1) {
    if (map[i].kind !== STEP_KINDS.PERSONALIZE) continue;
    if (personalizeCount === fieldIdx) return map[i].index;
    personalizeCount += 1;
  }
  return null;
}

function findOrderPathRequiredAiCancelledLabel(builderState, options) {
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state) return null;
  const doorwayOpts = { ...(options || {}), builderData: resolveBuilderData(options) };
  let match = null;
  const scan = (saved, label) => {
    if (isRequiredAiCancelledDoorwayBlocked(saved, { ...doorwayOpts, label })) {
      match = label;
      return true;
    }
    return false;
  };
  if (state.aiImageByLabel && forEachByLabelEntry(state.aiImageByLabel, () => {}, scan)) return match;
  if (state.sub?.aiImageByLabel && forEachByLabelEntry(state.sub.aiImageByLabel, () => {}, scan)) {
    return match;
  }
  return match;
}

/**
 * Safe MAIN-flow step when Offers/Add-ons is occupied but doorway is closed.
 * @param {object} [builderState]
 * @param {{ data?: object, deps?: object, navContext?: object, includeDom?: boolean }} [navOpts]
 * @returns {number|null}
 */
export function resolveOffersOccupancySafeStep(builderState, navOpts) {
  const nav = navOpts || {};
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  if (!state || state.mode !== 'MAIN') return null;
  const data = nav.data || resolveBuilderData(nav);
  const deps = nav.deps || {};
  const navContext = nav.navContext ?? null;
  const currentStep = Number(state.main?.step) || 1;
  const kind = getStepKind(currentStep, data, deps, navContext);
  if (!isOffersAddonsStepKind(kind)) return null;
  if (offersDoorwayOpen(state, { includeDom: nav.includeDom !== false, builderData: data })) return null;

  const requiredCancelLabel = findOrderPathRequiredAiCancelledLabel(state, { builderData: data });
  if (requiredCancelLabel) {
    const aiStep = resolvePersonalizeStepForLabel(requiredCancelLabel, data, deps, navContext);
    if (aiStep != null) return aiStep;
  }
  return resolveLastPersonalizeStepIndex(data, deps, navContext);
}

/**
 * Draft resume: clamp saved Offers/Add-ons open step when doorway is closed.
 * @param {number} openStep
 * @param {object} [deps]
 */
function isCartEditEntrySession(builderState) {
  const state = builderState ?? (typeof window !== 'undefined' ? window.builderState : null);
  if (!state) return false;
  if (state.cartEdit) return true;
  return isCartEditEntryManifest(readEntryManifest(state));
}

export function clampDraftResumeOpenStep(openStep, deps) {
  const options = deps || {};
  const builderState = options.builderState ?? (typeof window !== 'undefined' ? window.builderState : null);
  if (isCartEditEntrySession(builderState)) return Math.max(1, Number(openStep) || 1);
  const builderData = options.builderData ?? resolveBuilderData(options);
  const navContext = options.navContext ?? null;
  const navKernel = options.navKernel ?? options.getNavKernel?.();
  const navDeps = options.deps || {};
  const step = Math.max(1, Number(openStep) || 1);
  const kind = getStepKind(step, builderData, navDeps, navContext);
  if (!isOffersAddonsStepKind(kind)) return step;
  if (!builderState) return step;

  const probeState = {
    ...builderState,
    main: {
      ...(builderState.main || {}),
      step,
    },
  };
  const safeStep = resolveOffersOccupancySafeStep(probeState, {
    data: builderData,
    deps: navDeps,
    navContext,
    includeDom: false,
  });
  if (safeStep == null) return step;
  if (navKernel && typeof navKernel.buildStepMap === 'function') {
    const map = navKernel.buildStepMap();
    if (map.length) {
      const maxIndex = map[map.length - 1].index;
      return Math.min(safeStep, maxIndex);
    }
  }
  return safeStep;
}

let occupancyRedirecting = false;

/**
 * Redirect off Offers/Add-ons when doorway closes while step is active.
 * @param {object} [builderState]
 * @param {{ data?: object, deps?: object, navContext?: object }} [navOpts]
 * @returns {boolean} true when redirect fired
 */
export function enforceOffersAddonsOccupancy(builderState, navOpts) {
  if (typeof window === 'undefined' || occupancyRedirecting) return false;
  const state = builderState || window.builderState;
  if (!state || state.mode !== 'MAIN') return false;
  if (isCartEditEntrySession(state)) return false;
  const nav = navOpts || {};
  const data = nav.data || window.BUILDER_DATA;
  const safeStep = resolveOffersOccupancySafeStep(state, {
    data,
    deps: nav.deps,
    navContext: nav.navContext,
    includeDom: true,
  });
  if (safeStep == null) return false;
  const currentStep = Number(state.main?.step) || 1;
  if (currentStep === safeStep) return false;

  const pb = window.productBuilder;
  const kernel = pb && typeof pb._ensureNavKernel === 'function' ? pb._ensureNavKernel() : pb?._pbNavKernel;
  occupancyRedirecting = true;
  try {
    if (kernel && typeof kernel.navigate === 'function') {
      kernel.navigate({ targetStep: safeStep });
      return true;
    }
    if (pb && typeof pb.jumpToMainStep === 'function') {
      pb.jumpToMainStep(safeStep);
      return true;
    }
  } finally {
    occupancyRedirecting = false;
  }
  return false;
}

export function getOffersDoorwayBlockReason(builderState, options) {
  if (hasOrderPathFailedPendingRetry(builderState, options)) {
    return { kind: 'offers-doorway-fail', message: OFFERS_DOORWAY_FAIL_BLOCK_MSG };
  }
  return { kind: 'offers-doorway', message: OFFERS_DOORWAY_BLOCK_MSG };
}

export function showOffersDoorwayBlockHint(builderState, options) {
  if (typeof window !== 'undefined' && window.PBFooterNav?.showBlockHint) {
    const reason = getOffersDoorwayBlockReason(builderState, options);
    window.PBFooterNav.showBlockHint(reason.message);
  }
}

export function guardOffersAddonsDoorway(builderState, navOpts) {
  if (!shouldBlockOffersAddonsNavigation({ ...navOpts, builderState })) return true;
  showOffersDoorwayBlockHint();
  if (typeof window !== 'undefined' && window.PBFooterNav?.scheduleSync) {
    window.PBFooterNav.scheduleSync();
  }
  return false;
}

export function blocksEditEntry(builderState) {
  return hasOrderPathGenerationRunning(builderState);
}

export function blocksSubLeave(builderState) {
  return hasSubContextGenerationRunning(builderState);
}

export function getEditBlockReason() {
  return { kind: 'generation', message: EDIT_BLOCK_MSG };
}

export function getSubLeaveBlockReason() {
  return { kind: 'generation', message: SUB_LEAVE_BLOCK_MSG };
}

export function showEditBlockHint() {
  if (typeof window !== 'undefined' && window.PBFooterNav?.showBlockHint) {
    window.PBFooterNav.showBlockHint(EDIT_BLOCK_MSG);
    return;
  }
}

export function showSubLeaveBlockHint() {
  if (typeof window !== 'undefined' && window.PBFooterNav?.showBlockHint) {
    window.PBFooterNav.showBlockHint(SUB_LEAVE_BLOCK_MSG);
  }
}

let generationLockToken = '';

export function syncGenerationFlowLock(builderState) {
  if (typeof window === 'undefined' || !window.PBFlowLock) return;
  const flow = window.PBFlowLock;
  const locks = flow.LOCKS || {};
  const kind = locks.GENERATION || 'generation';
  const running = hasOrderPathGenerationRunning(builderState, { includeDom: true });

  if (running && !generationLockToken) {
    generationLockToken = flow.acquire(kind, { scopeEl: null });
  } else if (!running && generationLockToken) {
    flow.release(generationLockToken);
    generationLockToken = '';
  }
}

export function refreshEditEntryChrome() {
  if (typeof document === 'undefined') return;
  const blocked = blocksEditEntry();
  const selectors = '.pb-personalize-btn, .pb-addon-edit';
  document.querySelectorAll(selectors).forEach((btn) => {
    if (!btn || !btn.classList) return;
    btn.classList.toggle('pb-edit-gate-locked', blocked);
    btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    if (blocked) btn.setAttribute('title', EDIT_BLOCK_MSG);
    else btn.removeAttribute('title');
  });
}

export function guardEditEntry(builderState) {
  if (!blocksEditEntry(builderState)) return true;
  showEditBlockHint();
  refreshEditEntryChrome();
  return false;
}

export function guardSubLeave(builderState) {
  if (!blocksSubLeave(builderState)) return true;
  showSubLeaveBlockHint();
  if (typeof window !== 'undefined' && window.PBFooterNav?.scheduleSync) {
    window.PBFooterNav.scheduleSync();
  }
  return false;
}

export function cancelActiveGeneration(builderState) {
  const state = builderState || (typeof window !== 'undefined' ? window.builderState : null);
  const live = scanDomForRunningGeneration();
  if (live?.wrap && typeof window !== 'undefined' && window.PBAiImage?.cancelGeneration) {
    const cancelled = window.PBAiImage.cancelGeneration(live.wrap);
    if (cancelled) syncGenerationFlowLock(state);
    return cancelled;
  }
  return false;
}

export function patchProductBuilder(pb) {
  if (!pb || pb._pbAiSessionGatePatched) return;
  pb._pbAiSessionGatePatched = true;

  const wrapMethod = (name) => {
    const orig = pb[name];
    if (typeof orig !== 'function') return;
    pb[name] = function patchedAiSessionGate(...args) {
      if (name === 'switchMode') {
        const targetMode = args[0];
        if (targetMode === 'MAIN' && !guardSubLeave(window.builderState)) return;
      } else if (!guardEditEntry(window.builderState)) {
        return;
      }
      return orig.apply(this, args);
    };
  };

  wrapMethod('configureBundleItem');
  wrapMethod('openAddonBuilder');
  wrapMethod('switchMode');

  const origFinish = pb.finishSubFlow;
  if (typeof origFinish === 'function') {
    pb.finishSubFlow = function patchedFinishSubFlow(...args) {
      if (!guardSubLeave(window.builderState)) return;
      return origFinish.apply(this, args);
    };
  }
}

function onAiJobLifecycle() {
  syncGenerationFlowLock(typeof window !== 'undefined' ? window.builderState : null);
  refreshEditEntryChrome();
  enforceOffersAddonsOccupancy();
  if (typeof window !== 'undefined' && window.PBFooterNav?.scheduleSync) {
    window.PBFooterNav.scheduleSync();
  }
}

export function installAiSessionGate() {
  if (typeof window === 'undefined' || window._pbAiSessionGateInstalled) return;
  window._pbAiSessionGateInstalled = true;

  if (window.productBuilder) patchProductBuilder(window.productBuilder);

  document.addEventListener('pb-ai-job-started', onAiJobLifecycle);
  document.addEventListener('pb-ai-job-updated', onAiJobLifecycle);
  document.addEventListener('pb-flow-lock-change', refreshEditEntryChrome);

  document.addEventListener(
    'click',
    function (e) {
      const t = e.target;
      if (!t || !t.closest) return;
      const cancelBtn = t.closest('.pb-ai-image-cancel-btn');
      if (!cancelBtn) return;
      e.preventDefault();
      e.stopPropagation();
      cancelActiveGeneration();
    },
    true
  );
}

if (typeof window !== 'undefined') {
  window.PBAiSessionGate = {
    isActiveJobStatus,
    isFailedPendingRetryStatus,
    isSavedEntryRunning,
    isSavedEntryDoorwayBlocked,
    isRequiredAiCancelledDoorwayBlocked,
    hasOrderPathGenerationRunning,
    hasOrderPathFailedPendingRetry,
    hasOrderPathDoorwayBlocked,
    hasSubContextGenerationRunning,
    NAV_INTENTS,
    classifyNavigationIntent,
    blocksSubGenerationNavigation,
    isOffersAddonsStepKind,
    wouldEnterOffersAddonsDoorway,
    isOffersAddonsTargetStep,
    offersDoorwayOpen,
    blocksOffersAddonsDoorway,
    shouldBlockOffersAddonsNavigation,
    blocksForwardToOffersAddonsDoorway,
    resolveLastPersonalizeStepIndex,
    resolvePersonalizeStepForLabel,
    resolveOffersOccupancySafeStep,
    clampDraftResumeOpenStep,
    enforceOffersAddonsOccupancy,
    getOffersDoorwayBlockReason,
    showOffersDoorwayBlockHint,
    guardOffersAddonsDoorway,
    blocksEditEntry,
    blocksSubLeave,
    getEditBlockReason,
    getSubLeaveBlockReason,
    guardEditEntry,
    guardSubLeave,
    cancelActiveGeneration,
    syncGenerationFlowLock,
    refreshEditEntryChrome,
    patchProductBuilder,
    installAiSessionGate,
    scanDomForRunningGeneration,
    scanDomForDoorwayBlock,
    EDIT_BLOCK_MSG,
    SUB_LEAVE_BLOCK_MSG,
    OFFERS_DOORWAY_BLOCK_MSG,
    OFFERS_DOORWAY_FAIL_BLOCK_MSG,
  };
  installAiSessionGate();
}
